import React from 'react'
import classnames from 'classnames'

export interface BadgeProps {
  status?: string
  className?: string
  /** `soft` = no ring, less rounded; used for account header chips. */
  variant?: 'default' | 'soft'
  /** Force grey styling (e.g. inactive legend values in tooltips). */
  muted?: boolean
  /** Override the auto-formatted status label (keeps status-based colors). */
  displayLabel?: string
  /** When false, label is never ellipsized. Default true. */
  truncate?: boolean
}

export const Badge: React.FC<BadgeProps> = (props) => {
  const {
    status,
    className,
    variant = 'default',
    muted = false,
    displayLabel,
    truncate = true,
    ...rest
  } = props
  const isSoft = variant === 'soft'

  const formatBadgeLabel = (value: string): string => {
    const specialLabels: Record<string, string> = {
      ONHOLD: 'On Hold',
      PENDING_CLOSED: 'Pending Close',
      PENDING_CLOSE: 'Pending Close',
      PENDING_CHANGE: 'Pending Change',
    }
    if (specialLabels[value] != null) {
      return specialLabels[value]
    }
    // Title-case SCREAMING_SNAKE / ALLCAPS enums (CONSOLIDATED, ENABLED, PENDING_ON, …)
    if (value.length > 0 && value === value.toUpperCase() && /[A-Z]/.test(value)) {
      return value
        .split('_')
        .filter((word) => word.length > 0)
        .map((word) => word.charAt(0) + word.slice(1).toLowerCase())
        .join(' ')
    }
    return value.replaceAll('_', ' ')
  }

  const defaultStatusColorMap: { [key: string]: string[] } = {
    blue: [
      'VOID',
      'CREDIT',
      'CREDITED',
      'SCHEDULED',
      'CONSOLIDATED',
      'CONSOLIDATED_ESTIMATED',
      'UTILITY',
      'PG&E',
      'PGE',
      'REALTIME',
      'UTILITY_BILL',
      'UTILITY_BILL_ACCRUED',
      'UTILITY_BILL_PASSTHRU',
      'INVOICE_PUBLISHED',
      'CHARGING',
    ],
    indigo: [
      'CORP',
      'STANDALONE',
      'STANDALONE_ESTIMATED',
      'INVOICE_ACCRUED',
      'UNBILLED_INVOICE_ACCRUED',
      'SHADOW_INVOICE_ACCRUED',
      'BATCH',
      'BENEFITTING',
      'GENERATOR',
      'SDG&E',
      'SDGE',
    ],
    red: [
      'FAILED',
      'PAST_DUE',
      'PAYMENT_PLAN_PAST_DUE',
      'CHARGEBACK',
      'CANCELLED',
      'DISABLED',
      'BALANCE_CARRY_FORWARD',
      'NO',
      'LATE',
      'UNPAID',
      'BLOCKED',
      'NOT_ON_VNEM',
      'OFF_TABLE',
      'CLOSED',
      'INACTIVE',
      'FINAL',
      'SKIPPED',
      'INVOICE_SKIPPED',
      'INVOICE_VOIDED',
      'INVOICE_REVERSED',
      'UTILITY_BILL_REVERSED',
      'UTILITY_BILL_PASSTHRU_REVERSED',
      'TENANT_PAYMENT_REVERSED',
      'TENANT_UTILITY_PAYMENT_REVERSED',
      'CORP_UTILITY_PAYMENT_REVERSED',
      'UNBILLED_INVOICE_ACCRUED_REVERSED',
      'SHADOW_INVOICE_REVERSED',
      'TENANT_WRITE_OFF_REVERSED',
      'MISC_REVENUE_REVERSED',
      'SOLAR_DISCOUNT_PRIOR_REVERSED',
      'UTILITY_CREDIT_REIMBURSE_PAYMENT_REVERSED',
    ],
    yellow: [
      'PENDING',
      'PROCESSING',
      'PARTIALLY_PAID',
      'REVERSED',
      'PARTIALLY_CREDITED',
      'ONHOLD',
      'ON_HOLD',
      'PENDING_ACTIVE',
      'PENDING_CLOSED',
      'PENDING_CLOSE',
      'PENDING_STOP',
      'PENDING_ON',
      'PENDING_OFF',
      'PENDING_CHANGE',
      'STOPPED',
      'QUEUED',
      'UTILITY_TRANSITION_ADJUSTMENT',
      'UTILITY_ADJUSTMENT',
      'SOLAR_ADJUSTMENT',
      'MISC_ADJUSTMENT',
      'UTILITY_CREDIT_ADJUSTMENT',
      'UTILITY_BILL_BALANCE_ADJUSTMENT',
      'INVOICE_BALANCE_ADJUSTMENT',
      'UNKNOWN_UTILITY_PAYMENT',
      'WAITING_FOR_APPROVAL',
    ],
    green: [
      'SUCCEEDED',
      'PAID',
      'PAID_EXTERNALLY',
      'CONSUMED',
      'ENABLED',
      'CURRENT',
      'PAYMENT_PLAN_CURRENT',
      'ACTIVE',
      'OPEN',
      'YES',
      'ENROLLED',
      'ON_VNEM',
      'ON_TABLE',
      'POSTED',
      'TENANT_PAYMENT',
      'TENANT_UTILITY_PAYMENT',
      'CORP_UTILITY_PAYMENT',
      'CORP_TENANT_PAYMENT',
      'APPROVED',
    ],
    orange: ['BILLED', 'UTILITY_BILL_CARRY_FORWARD', 'INVOICE_CARRY_FORWARD', 'BALANCE_ONLY', 'SCE'],
    gray: ['ARCHIVED'],
  }

  const getColorClasses = (): string => {
    if (muted) {
      return isSoft
        ? 'bg-slate-500/25 text-slate-300 dark:bg-slate-500/20 dark:text-slate-400'
        : 'bg-slate-500/25 text-slate-300 ring-slate-500/30 dark:bg-slate-500/20 dark:text-slate-400 dark:ring-slate-400/20'
    }
    for (const color in defaultStatusColorMap) {
      if (status != null && defaultStatusColorMap[color].includes(status)) {
        switch (color) {
          case 'blue':
            return isSoft
              ? 'bg-blue-100 dark:bg-blue-400/10 text-blue-800 dark:text-blue-400'
              : 'bg-blue-100 dark:bg-blue-400/10 text-blue-800 dark:text-blue-400 ring-blue-600/20 dark:ring-blue-400/50'
          case 'indigo':
            return isSoft
              ? 'bg-indigo-100 dark:bg-indigo-400/10 text-indigo-800 dark:text-indigo-300'
              : 'bg-indigo-100 dark:bg-indigo-400/10 text-indigo-800 dark:text-indigo-300 ring-indigo-600/20 dark:ring-indigo-400/40'
          case 'red':
            return isSoft
              ? 'bg-red-100 dark:bg-red-400/10 text-red-800 dark:text-red-400'
              : 'bg-red-100 dark:bg-red-400/10 text-red-800 dark:text-red-400 ring-red-600/20 dark:ring-red-400/50'
          case 'yellow':
            return isSoft
              ? 'bg-yellow-100 dark:bg-yellow-400/10 text-yellow-800 dark:text-yellow-400'
              : 'bg-yellow-100 dark:bg-yellow-400/10 text-yellow-800 dark:text-yellow-400 ring-yellow-600/30 dark:ring-yellow-400/30'
          case 'green':
            return isSoft
              ? 'bg-green-100 dark:bg-green-400/10 text-green-800 dark:text-green-400'
              : 'bg-green-100 dark:bg-green-400/10 text-green-800 dark:text-green-400 ring-green-600/40 dark:ring-green-400/20'
          case 'orange':
            return isSoft
              ? 'bg-orange-100 dark:bg-orange-400/10 text-orange-800 dark:text-orange-400'
              : 'bg-orange-100 dark:bg-orange-400/10 text-orange-800 dark:text-orange-400 ring-orange-600/40 dark:ring-orange-400/20'
          case 'gray':
            return isSoft
              ? 'bg-slate-100 dark:bg-slate-400/10 text-slate-800 dark:text-slate-300'
              : 'bg-slate-100 dark:bg-slate-400/10 text-slate-800 dark:text-slate-300 ring-slate-500/20 dark:ring-slate-400/20'
        }
      }
    }
    return isSoft
      ? 'bg-gray-50 dark:bg-gray-400/10 text-gray-800 dark:text-gray-400'
      : 'bg-gray-50 dark:bg-gray-400/10 text-gray-800 dark:text-gray-400 ring-gray-600/10 dark:ring-gray-400/20'
  }

  if (status == null) {
    return null
  }

  const label = displayLabel ?? formatBadgeLabel(status)
  // Native title: prefer the visible label when truncated (displayLabel or formatted status).
  const titleText = displayLabel ?? (label !== status || className?.includes('max-w') === true ? status : undefined)

  return (
    <>
      <span
        className={classnames(
          'inline-flex max-w-full min-w-0 items-center text-xs font-medium',
          truncate ? 'overflow-hidden' : 'overflow-visible whitespace-nowrap',
          isSoft ? 'rounded-md px-2 py-0.5' : 'rounded-full px-2.5 py-1 ring-1 ring-inset',
          getColorClasses(),
          className
        )}
        {...rest}
        title={titleText}
      >
        <span className={classnames(truncate && 'min-w-0 truncate')}>{label}</span>
      </span>
    </>
  )
}

export default Badge
